import './MemoryGame.scss';
import { useEffect, useState } from 'react';
import Confetti from 'Feature/Confetti/Confetti';
import { Button } from '@lmig/lmds-react-button';

interface IMemoryGame {
  'icon-one': IGlassImage;
  'icon-two': IGlassImage;
  'icon-three': IGlassImage;
  'icon-four': IGlassImage;
  'icon-five': IGlassImage;
  'icon-six': IGlassImage;
  'icon-seven': IGlassImage;
  'icon-eight': IGlassImage;
  'background-color-one': string;
  'background-color-two': string;
  'background-color-three': string;
  'background-color-four': string;
  'background-color-five': string;
  'background-color-six': string;
  'background-color-seven': string;
  'background-color-eight': string;
  'card-back-color': string;
  'card-matched-color': string;
  'button-in-progress-text': string;
  'button-completed-text': string;
  'game-border-color': string;
  'game-board-size': string;
  'card-border-color': string;
  'card-border-width': string;
  'card-border-radius': string;
}

// Card interface for the memory game
interface Card {
  id: number;
  value: number;
  icon: IGlassImage;
  backgroundColor: string;
  isFlipped: boolean;
  isMatched: boolean;
}

const numberToWord = (num: number) => {
  switch (num) {
    case 1:
      return 'one';
    case 2:
      return 'two';
    case 3:
      return 'three';
    case 4:
      return 'four';
    case 5:
      return 'five';
    case 6:
      return 'six';
    case 7:
      return 'seven';
    case 8:
      return 'eight';
    default:
      return '';
  }
};

const MemoryGame = (props: IMemoryGame) => {
  const totalCards = 16;
  const gameBoardSize = props['game-board-size'];
  const gameBorderColor = props['game-border-color'];
  const cardBorderColor = props['card-border-color'];
  const cardBorderWidth = props['card-border-width'];
  const cardBorderRadius = props['card-border-radius'];
  const cardBackColor = props['card-back-color'];
  const cardMatchedColor = props['card-matched-color'];
  const buttonInProgressText = props['button-in-progress-text'];
  const buttonCompletedText = props['button-completed-text'];

  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [buttonText, setButtonText] = useState(buttonCompletedText);
  const [cards, setCards] = useState<Card[]>([]);
  const [selectedCards, setSelectedCards] = useState<Card[]>([]);
  const [isLocked, setIsLocked] = useState(false);
  const [matchedPairs, setMatchedPairs] = useState(0);

  const runConfetti = () => {
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 3000);
  };

  const initializeGame = () => {
    const values = [1, 2, 3, 4, 5, 6, 7, 8];
    const duplicatedCards = [...values, ...values].map((value, index) => ({
      id: index,
      icon: props[`icon-${numberToWord(value)}` as keyof IMemoryGame] as IGlassImage,
      backgroundColor: (props[`background-color-${numberToWord(value)}` as keyof IMemoryGame] as string).replaceAll(
        '\"',
        '',
      ),
      value,
      isFlipped: false,
      isMatched: false,
    }));

    const shuffledCards: Card[] = shuffleArray(duplicatedCards);

    setCards(shuffledCards);
    setSelectedCards([]);
    setMatchedPairs(0);
    setIsLocked(false);
    setButtonText(buttonInProgressText);
  };

  const shuffleArray = (array: Card[]): Card[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const isSolved = () => {
    return matchedPairs + 1 === totalCards / 2;
  };

  const handleCardClick = (clickedCard: Card) => {
    if (isLocked || clickedCard.isFlipped || clickedCard.isMatched) {
      return;
    }

    // Flip the clicked card
    setCards((prev) => prev.map((card) => (card.id === clickedCard.id ? { ...card, isFlipped: true } : card)));

    const newSelectedCards = [...selectedCards, clickedCard];
    setSelectedCards(newSelectedCards);

    // Check for match when 2 cards are selected
    if (newSelectedCards.length === 2) {
      setIsLocked(true);
      checkForMatch(newSelectedCards);
      resetTurn();
    }
  };

  // Check for match
  const checkForMatch = (selected: Card[]) => {
    const [first, second] = selected;

    if (first.value === second.value) {
      // Match found
      setCards((prev) =>
        prev.map((card) => (card.id === first.id || card.id === second.id ? { ...card, isMatched: true } : card)),
      );
      setMatchedPairs((prev) => prev + 1);

      // Check if game is won
      if (isSolved()) {
        runConfetti();
        setButtonText(buttonCompletedText);
      }
    } else {
      // No match - flip back after delay
      setTimeout(() => {
        setCards((prev) =>
          prev.map((card) => (card.id === first.id || card.id === second.id ? { ...card, isFlipped: false } : card)),
        );
      }, 500);
    }
  };

  const resetTurn = () => {
    setSelectedCards([]);
    setIsLocked(false);
  };

  useEffect(() => {
    if (firstLoad) {
      setFirstLoad(false);
      initializeGame();
      return;
    }
  }, []);

  return (
    <div className="memory-game" data-theme="evolved">
      {showConfetti && <Confetti />}
      <div
        className="game-board"
        style={{
          width: gameBoardSize ? gameBoardSize : '400px',
          border: gameBorderColor ? `1px solid ${gameBorderColor}` : 'none',
        }}
      >
        {cards.map((card) => (
          <div
            key={card.id}
            style={{
              border: cardBorderWidth ? `${cardBorderWidth} solid ${cardBorderColor}` : 'none',
              borderRadius: cardBorderRadius,
              backgroundColor: card.isMatched
                ? cardMatchedColor
                : card.isFlipped
                  ? card.backgroundColor
                  : cardBackColor,
            }}
            className={`card ${card.isFlipped ? 'flipped' : ''} ${card.isMatched ? 'matched' : ''}`}
            onClick={() => handleCardClick(card)}
          >
            {card.isFlipped || card.isMatched ? (
              <img
                src={card.icon.Src}
                alt={card.icon.Alt}
                className={card.icon.Class}
                height={card.icon.Height}
                width={card.icon.Width}
              />
            ) : (
              ''
            )}
          </div>
        ))}
      </div>
      <Button className="button" onClick={initializeGame} size="small" variant="secondary">
        {buttonText}
      </Button>
    </div>
  );
};

export default MemoryGame;
